import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { debounce, Typography } from '@mui/material';

import { MultiSelect } from 'common/components/FormField';
import {
    IExtraCategoryLabelFragment,
    IOfferEventSortBy,
    IOfferEventType,
    InputOptionOfferEventsDocument,
} from 'graphql/types';
import { TInputOption } from 'common/types';

interface IProps {
    isFilter?: boolean;
    label?: string;
    ids?: string[];
    offerEventTypes?: IOfferEventType[];
    onChange?: (name: string, value: string[]) => void;
}

const BASE_QUERY_VARS = {
    first: 50,
    sortBy: IOfferEventSortBy.Title,
};

export const OfferEventSelectField = ({
    isFilter,
    label,
    ids = [],
    offerEventTypes,
    onChange,
    ...other
}: IProps & FieldProps) => {
    const [inputValue, setInputValue] = useState('');
    const [translate] = useTranslation();

    const [fetchOfferEvents, { data, loading, called }] = useLazyQuery(
        InputOptionOfferEventsDocument
    );

    const { paginatedOfferEvents } = data || {};

    const offerEvents =
        paginatedOfferEvents?.edges.map((edge) => edge?.node).filter(Boolean) ||
        [];
    const offerEventsCount = paginatedOfferEvents?.count || 0;

    const inputOptions: TInputOption[] = useMemo(
        () =>
            offerEvents.map(({ id, title, extraCategoryStringLabels }) => ({
                id,
                name: title,
                extraCategoryLabels: extraCategoryStringLabels || [],
                isInfo: false,
            })) || [],
        [offerEvents]
    );

    const value: TInputOption[] = other.field.value;

    // Add value options to input options if they are not already in the input options
    value.forEach((valueOption) => {
        if (
            !inputOptions.some(
                (option: TInputOption) => option.id === valueOption.id
            )
        ) {
            const { id, name, extraCategoryLabels } = valueOption;

            inputOptions.push({
                id,
                name,
                extraCategoryLabels,
                isInfo: false,
            });
        }
    });

    if (offerEventsCount > BASE_QUERY_VARS.first) {
        inputOptions.push({
            id: '',
            name: translate('moreResultsSelectSearch'),
            extraCategoryLabels: [],
            isInfo: true,
        });
    }

    const {
        form: { setFieldValue },
    } = other;

    const handleChange = (name: string, value: string[]) => {
        onChange?.(name, value);
        setFieldValue(name, value);
    };

    const fetch = useMemo(
        () =>
            debounce((inputValue: string) => {
                fetchOfferEvents({
                    variables: {
                        ...BASE_QUERY_VARS,
                        ids: !!inputValue ? undefined : ids,
                        q: inputValue || undefined,
                        types: offerEventTypes || undefined,
                    },
                });
            }, 400),
        [offerEventTypes]
    );

    // Searching for results
    useEffect(() => {
        // The first query is done by opening, so we want to avoid calling it twice
        if (!called) return;

        fetch(inputValue);
    }, [inputValue, fetch]);

    // Fetch offer events when ids change
    useEffect(() => {
        // Do the initial call with offerEventTypes if they are provided
        if (!!offerEventTypes?.length && !ids.length && !called) {
            fetch(inputValue);

            return;
        }

        if (!ids.length || !called) return;

        fetchOfferEvents({
            variables: {
                ...BASE_QUERY_VARS,
                ids: ids,
                types: offerEventTypes || undefined,
            },
        });
    }, [ids, offerEventTypes]);

    const handleOpen = () => {
        if (!ids.length || !!offerEvents.length) return;

        fetchOfferEvents({
            variables: {
                ...BASE_QUERY_VARS,
                ids: ids,
                q: inputValue || undefined,
            },
        });
    };

    return (
        <Field
            component={MultiSelect}
            filterOptions={(option: TInputOption) => option}
            getOptionDisplay={(option: TInputOption) => {
                const extraCategoryString = option.extraCategoryLabels
                    .map(
                        (extraCategoryLabel) =>
                            (extraCategoryLabel as IExtraCategoryLabelFragment)
                                .value
                    )
                    .join(', ');

                return (
                    <>
                        {option.name}

                        {!!extraCategoryString && (
                            <Typography
                                noWrap
                                color="grey.500"
                                component="div"
                                sx={{
                                    mt: 0.5,
                                    minWidth: '0',
                                    width: '100%',
                                }}
                                variant="caption"
                            >
                                {extraCategoryString}
                            </Typography>
                        )}
                    </>
                );
            }}
            getOptionIsInfo={(option: TInputOption) => option.isInfo}
            getOptionKey={(option: TInputOption) => option.id}
            getOptionLabel={(option: TInputOption) => option.name}
            InputProps={{ label }}
            isOptionEqualToValue={(option: TInputOption, value: TInputOption) =>
                option.id === value.id
            }
            limitTags={5}
            loading={loading}
            loadingText={translate('loadingDots')}
            noOptionsText={translate(
                `noOptionsText.${isFilter ? 'filterOffers' : 'offers'}`
            )}
            options={inputOptions}
            onChange={handleChange}
            onInputChange={(
                _event: React.ChangeEvent<HTMLInputElement>,
                newInputValue: string
            ) => {
                setInputValue(newInputValue);
            }}
            onOpen={handleOpen}
            {...other}
        />
    );
};
